// Example story data for TweePorter.ca
// Each story is preloaded into the editor on its dedicated page

export const exampleStories = {
  'personal-the-missed-call': {
    title: 'The Missed Call',
    category: 'Personal Stories & Creative Projects',
    description: 'A mysterious phone call from someone who sounds exactly like you warns about tomorrow.',
    twee: `:: StoryTitle
The Missed Call

:: Start
Your phone buzzes on the table. Unknown number. It's late, and the rain outside drums on the window like impatient fingers.

You hesitate. The call keeps ringing.

!What do you do?

[[Answer the call|Answer]]
[[Let it go to voicemail|Ignore]]

:: Answer
You swipe to accept.

A voice that sounds exactly like yours whispers, "Good, we don't have much time. The elevator at work is going to fail tomorrow. Take the stairs. And whatever you do, don't sign the new contract without reading page 17."

Before you can respond, the line goes dead.

[[Take the warning seriously|HeedWarning]]
[[Assume it was a prank|BrushItOff]]

:: Ignore
You stare at the screen until the ringing stops.

A moment later, a voicemail notification appears. But when you check, the inbox is empty—no new messages, no missed call in the log. It's as if it never happened.

[[Call the number back|CallBack]]
[[Forget about it and go to bed|ForgetIt]]

:: CallBack
You dial the number. A recorded message answers:

"The number you have reached does not exist. It never existed. Some warnings are only offered once."

A chill crawls up your spine.

[[Decide it was a glitch in the system|Glitch]]
[[Decide someone is messing with you|Paranoid]]

:: ForgetIt
You shrug it off. People misdial. Tech glitches. Life goes on.

The next day, you take the elevator at work like always. Halfway between floors, the car shudders and stops. The lights flicker.

It takes two hours for maintenance to get you out. Nobody is badly hurt, but you can't shake the feeling that you missed a different ending.

''Sometimes the scariest stories are the ones where nothing quite happens—but almost does.''

[[Replay the story|Start]]

:: HeedWarning
The next morning, you choose the stairs. Halfway up, you hear a violent clunk from the elevator shaft and the alarms start blaring.

Later, you quietly insist on reviewing the new contract. On page 17, in dense boilerplate, you find a clause that would have made you personally liable for "unexpected integration risks."

You circle it in red.

The legal team thanks you. Your boss looks at you, puzzled. "How did you know?"

You just smile. 

''You answered a call from tomorrow, and tomorrow thanked you for it.''

[[Replay the story|Start]]

:: BrushItOff
You decide it's a friend pranking you with a voice filter. The next day you ignore the elevator jitters and sign the new contract your boss hands you.

Three months later, the new system fails spectacularly. The investigation points to overlooked integration risks—on page 17. Your name is on the approval line.

You start getting calls from unknown numbers all the time now. You never answer them.

''The future keeps calling. You keep letting it ring.''

[[Replay the story|Start]]

:: Glitch
You write the whole thing off as a network glitch. But that night, your phone lights up again—no ringtone, just the lock screen.

One notification: "Missed call from: YOU"

When you swipe, there is no call log, no notification history. Just your reflection in the dark screen, staring back.

''Some stories don't need answers. Just better questions.''

[[Replay the story|Start]]

:: Paranoid
You decide someone is messing with you and post a rant online about spam callers.

The post gets exactly one reply, from an anonymous account with no history:

>"I tried to warn you too. It never works when we sound too much like them."

The account disappears a moment later.

You realize the avatar was your profile picture—just a little older.

''Maybe paranoia is what happens when your future self loses patience.''

[[Replay the story|Start]]`
  },
  
  'personal-library-that-wasnt-there': {
    title: 'The Library That Wasn\'t There',
    category: 'Personal Stories & Creative Projects',
    description: 'A mysterious library appears in an alley, filled with books bearing your name—stories you started but never finished.',
    twee: `:: StoryTitle
The Library That Wasn't There

:: Start
You take a wrong turn on the way home and find a narrow alley you've never noticed before. At the end of it stands a tiny brick building with a flickering sign:

''THE LIBRARY OF UNREAD STORIES''

The door is half open.

[[Step inside|Enter]]
[[Take a photo and walk away|WalkAway]]

:: Enter
Inside, shelves are stacked with books that all have your name on the spine.

A librarian with ink-stained fingers looks up. "These are the stories you started but never finished," she says. "You may read one. Or you may write one. But you may not leave them all unread."

[[Pick up a finished-looking book|FinishedBook]]
[[Pick up a blank notebook|BlankBook]]

:: WalkAway
You snap a photo for social media, but your phone shows only an empty brick wall.

When you look back, the alley is just… an alley.

For the rest of the week, every time you sit down to read or write, you feel oddly restless, as if someone is waiting for you to show up.

''Not every door will wait forever.''

[[Replay the story|Start]]

:: FinishedBook
You choose a book that looks complete and thumb through the pages.

It's a novel about a version of you who never took the risks you were afraid of. It's beautifully written, quietly heartbreaking, and ends with a single question:

> "If you had one more chance to choose, would you turn back now?"

The last page is blank, except for a line in your own handwriting: ''You are reading this too soon.''

[[Respect the warning and close the book|CloseBook]]
[[Ignore it and read the final page anyway|ReadAnyway]]

:: BlankBook
You open the blank notebook. The first page writes itself:

> "Chapter One: The day you finally stopped only imagining your life."

Words appear as you think them—every unwritten idea, every abandoned project, every 'someday' you kept postponing.

The librarian nods. "You get to finish one. Choose carefully."

[[Write the story you always wanted to share|WriteDream]]
[[Use it to rewrite a memory instead|RewriteMemory]]

:: CloseBook
You close the book. The librarian smiles. "You weren't ready for that ending yet. Good."

One slim volume on the shelf vanishes. You feel lighter, as if a decision that had been hanging over you quietly resolved itself.

On your way out, you notice a small sign:

> "This library doesn't keep books. It keeps promises."

When you reach the street, the building is gone. But your to-do list looks different in your mind now—shorter, sharper, real.

[[Replay the story|Start]]

:: ReadAnyway
You turn to the final page. It's a single paragraph describing your exact day tomorrow—down to the messages you'll ignore and the invitation you'll decline.

At the bottom is a note:

> "This is how your story ends if you change nothing."

When you look up, the shelves are empty. Every book with your name on it has vanished.

The librarian shrugs. "We only keep unwritten stories."

You step back into the street, suddenly desperate to prove the book wrong.

''A spoiler is only a spoiler if you refuse to improvise.''

[[Replay the story|Start]]

:: WriteDream
You decide to write the story you have always dreamed of finishing.

As you think, the pages fill with scenes, characters, and choices you've been carrying around for years. When you pause, the librarian takes the notebook and stamps the back cover with a quiet thud.

"You'll find this waiting on your desk tomorrow," she says. "Try not to leave it unread this time."

The next morning, there it is: a printed draft with your name on it and a sticky note:

> "From the Library. Due date: Before you forget why you started."

[[Replay the story|Start]]

:: RewriteMemory
You decide to rewrite a memory instead: a conversation you wish had gone differently, a chance you wish you had taken.

As the ink dries, the scene in your mind changes—not erased, but softened, like a scar that stops aching.

The librarian gently closes the notebook. "Some stories can't be changed," she says, "but they can be retold."

When you step outside, the alley is gone, but your next conversation feels strangely easier.

[[Replay the story|Start]]`
  },
  
  'business-cloud-migration-crossroads': {
    title: 'Cloud Migration Crossroads',
    category: 'Business Case Studies That Convert',
    description: 'As IT director, you must choose between a cheap lift-and-shift, a careful phased migration, or extending current hardware.',
    twee: `:: StoryTitle
Cloud Migration Crossroads

:: Start
You are the IT director for a mid-sized company whose aging servers are becoming unreliable. The CEO wants to "get everything into the cloud this quarter."

Three proposals land on your desk:

1. A cheap **lift-and-shift** with minimal redesign.
2. A carefully planned **phased migration** with optimization.
3. Doing the bare minimum: **extend the life** of your current hardware.

!Which path do you recommend?

[[Cheap lift-and-shift|LiftShift]]
[[Phased migration with optimization|Phased]]
[[Extend current hardware|Extend]]

:: LiftShift
The vendor promises a fast migration: copy existing systems into virtual machines, minimal code changes, done in six weeks.

The migration finishes on time. Everyone applauds—until the first month's cloud bill arrives. It's 3× higher than expected. Performance is worse during peak hours, and your team is constantly firefighting.

Finance demands answers.

[[Blame the vendor and renegotiate|BlameVendor]]
[[Admit design shortcuts and propose a second optimization project|SecondPass]]

:: Phased
You propose a phased migration: first analyze workloads, then re-architect the most expensive parts before moving them.

The CEO is impatient but agrees when you show a simple cost-comparison chart.

Three months later, only 60% of systems have migrated—but those workloads run 40% cheaper than the old servers, with improved uptime.

Your team has a reusable pattern for the remaining work.

[[Share the early wins with leadership|ShareWins]]
[[Hide the partial progress until everything is done|StayQuiet]]

:: Extend
You choose to extend the current hardware life: buy a few more servers, apply patches, and delay cloud migration "until next year."

Initially, costs look stable. But a surprise hardware failure takes down your customer portal for six hours.

Several key clients call your CEO directly.

[[Stick with the delay plan|DoubleDownDelay]]
[[Reopen the cloud discussion with hard numbers|ReopenCloud]]

:: BlameVendor
You push hard on the vendor, threatening to switch providers. They offer a small discount and suggest adding reserved instances to manage costs.

Six months later, the bills are still higher than your old hardware, and nobody really understands why.

The CEO quietly begins talking to recruiters about "more cloud-fluent leadership."

''Cheap migrations are rarely cheap; they are just expensive in slow motion.''

[[Replay the story|Start]]

:: SecondPass
You admit that the first migration was rushed and propose a second project focused on optimization: right-sizing instances, shutting down idle services, and redesigning one noisy application.

The CEO is annoyed but appreciates the honesty when you show a clear business case.

Within six months, costs drop by 35% and stability improves dramatically. The board asks you to present "lessons learned" at the next meeting.

''Sometimes the most expensive choice is pretending you're done when you're only halfway there.''

[[Replay the story|Start]]

:: ShareWins
You share interim results: early cost savings, improved uptime, and a simplified architecture diagram that even non-technical leaders understand.

The CEO starts using your project as an example in investor calls. You're asked to mentor another team that's planning an AI pilot.

The remaining migrations proceed with fewer fights and more budget.

''Slow, visible progress can be more persuasive than fast, invisible heroics.''

[[Replay the story|Start]]

:: StayQuiet
You decide to wait until the entire migration is complete before sharing results. After all, why bother leadership with partial updates?

Three months later, a single misconfigured legacy system—still waiting for migration—causes a high-profile outage.

"Why didn't we know this was still on the old hardware?" the CEO asks.

Your quiet progress suddenly looks like a lack of communication.

[[Replay the story|Start]]

:: DoubleDownDelay
You argue that the outage was a freak event and that with a bit more redundancy, the on-prem solution will be fine.

Finance likes the predictable capital spend. Marketing, burned by the outage, does not.

The next time the board discusses "digital transformation," someone suggests starting with a different department—without you.

''Avoiding a decision is still a decision; it just sends the message that someone else should make the next one.''

[[Replay the story|Start]]

:: ReopenCloud
You walk into the next executive meeting with a simple slide: downtime hours, estimated revenue impact, and a side-by-side comparison of cloud vs. on-prem risks.

This time, nobody argues about whether to move—only how quickly.

You are asked to lead a structured migration initiative. The surprise twist: the hardware failure created just enough urgency to get the project funded properly.

''Sometimes the worst day for uptime is the best day for alignment—if you tell the story well.''

[[Replay the story|Start]]`
  },
  
  'business-the-free-discount': {
    title: 'The "Free" Discount',
    category: 'Business Case Studies That Convert',
    description: 'A major prospect demands 40% off your software platform. How you respond shapes the entire relationship.',
    twee: `:: StoryTitle
The "Free" Discount

:: Start
You are a sales manager at a B2B software company. A major prospect—Orion Logistics—has been trialing your platform for 30 days.

On the final day of the trial, their procurement officer calls:

"We like the product, but we need a deeper discount. Give us 40% off list, or we'll have to reconsider."

Your rep looks at you, panicked.

[[Approve the 40% discount immediately|ApproveBig]]
[[Offer a smaller discount with clear trade-offs|CounterOffer]]
[[Politely refuse the discount and focus on value|HoldLine]]

:: ApproveBig
You tell your rep to agree to the 40% discount. "We can make it up in volume," you joke.

The deal closes. The team celebrates. But when Finance reviews the numbers, they flag the account as barely profitable.

Three months later, Orion demands additional features "as part of the original deal." Saying no is politically difficult; saying yes erodes margins further.

[[Try to raise the price at renewal|RaiseLater]]
[[Swallow the cost and hope for expansions|HopeExpansion]]

:: CounterOffer
You suggest a 20% discount instead, with a smaller starter package and a clear ramp-up plan for year two.

Orion pushes back: "We really need 40%."

Your rep looks nervous.

[[Stand firm on the structured compromise|Hold20]]
[[Cave and give them 40% after all|CaveLate]]

:: HoldLine
You join the call and acknowledge their budget concern.

"We're not the cheapest option," you say, "but our platform saved similar logistics clients 15–20% on routing costs within six months. If price is the only decision factor, we're probably not the right partner—and that's okay."

Silence.

Then the procurement officer surprises you: "Give me an updated proposal with performance commitments instead of bigger discounts."

[[Propose a value-based contract|ValueContract]]
[[Stick to the original quote with no changes|NoChange]]

:: RaiseLater
At renewal time, you try to raise prices back toward list to recover margins.

Orion pushes back hard: "You set our anchor at 40% off. Any increase is a breach of trust."

They agree to a minor bump but begin evaluating competitors.

''The hidden cost of deep discounts is not just revenue—it's the story you taught the customer to believe about your value.''

[[Replay the story|Start]]

:: HopeExpansion
You agree to the extra features at no charge, hoping Orion will expand later.

They do expand—but only under the same discount expectations. Your team calls them "our busiest unprofitable logo."

When a budget cut hits, leadership starts asking which customers are worth keeping.

Orion's dashboard looks impressive. Their margin line does not.

[[Replay the story|Start]]

:: Hold20
You hold firm: "At 20% we can commit to service levels and a roadmap that actually favors you. At 40%, we would have to cut corners—and we don't want you to feel that six months from now."

Orion takes a week to decide.

When they come back, they agree to the 20% discount with the smaller starter package and a 12-month review.

A year later, after hitting clear ROI milestones, they expand at near-full price.

''Sometimes a smaller first deal is how you earn a bigger, healthier relationship.''

[[Replay the story|Start]]

:: CaveLate
You eventually cave and give them 40%—after several tense calls.

They close, but the emotional tone of the relationship is set: they push, you bend.

Internally, your rep learns a dangerous lesson about how to "win" deals.

''The discount they remember isn't just the number—it's how easily you surrendered it.''

[[Replay the story|Start]]

:: ValueContract
You propose a revised offer: a modest 10% discount, plus a value-based clause—

If Orion does not see at least 10% routing cost savings in 12 months, they get three months of service free.

Legal reviews it; procurement hesitates but agrees. They now have a story to tell internally: "We didn't get the biggest discount, we got the safest outcome."

Twelve months later, the savings hit 18%. Your account manager uses those numbers as a case study for the next prospect.

''Sometimes the best discount is the risk you are willing to share.''

[[Replay the story|Start]]

:: NoChange
You send the original quote with a polite note.

Orion thanks you for your time and chooses a cheaper competitor.

Six months later, you hear through the grapevine that their rollout has gone badly. Their new provider over-promised and under-delivered.

When Orion comes back to you for help, your pricing and your confidence both feel different.

''Walking away from the wrong deal is often how you make room for the right one.''

[[Replay the story|Start]]`
  },
  
  'training-first-day-on-the-factory-floor': {
    title: 'First Day on the Factory Floor',
    category: 'Training, Education, and Onboarding',
    description: 'Your first day as a factory supervisor presents an immediate safety decision that will define your leadership.',
    twee: `:: StoryTitle
First Day on the Factory Floor

:: Start
It's your first day as a supervisor on the factory floor.

Your mentor, Carla, walks you through the safety procedures, then leaves you to observe the morning shift.

An experienced operator, Miguel, waves you over. "The conveyor sensor has been twitchy all week," he says. "We usually just tap it to get it going."

You notice a small yellow tag that says "Report recurring faults to maintenance."

[[Tell Miguel to keep using the quick fix|AllowQuickFix]]
[[Ask Miguel to show you the issue in detail|ObserveIssue]]
[[Stop the line and call maintenance immediately|StopLine]]

:: AllowQuickFix
You say, "If it's working for now, let's not slow production on my first day."

Miguel taps the sensor. The conveyor jolts back to life.

Hours later, the sensor fails completely and jams a pallet at an awkward angle. No one is hurt, but production halts for an hour while maintenance digs in.

The plant manager asks why no one reported the earlier warning signs.

[[Admit you knew about the problem|AdmitProblem]]
[[Say you were never told about it|Deflect]]

:: ObserveIssue
You ask Miguel to show you exactly what happens and how often.

You watch the sensor misread three times in ten minutes. It's clearly not a one-off glitch.

Miguel shrugs. "We're used to it. Maintenance is slow, and we have quotas."

You realize this is about more than a sensor.

[[Escalate the issue to maintenance with Miguel present|EscalateWithMiguel]]
[[Log it quietly and hope it gets fixed later|LogQuietly]]

:: StopLine
You decide to stop the line and call maintenance right away.

Some operators groan. A few roll their eyes. "Great, a safety hero," someone mutters.

But within minutes, maintenance confirms the sensor is intermittently failing and could trigger a misalignment that might injure someone clearing a jam by hand.

Your mentor Carla backs your decision.

[[Use this as a teaching moment with the team|TeachMoment]]
[[Say nothing and let management handle the messaging|StaySilent]]

:: AdmitProblem
You tell the plant manager, "Miguel brought it up and I decided to keep the line running. That was a mistake."

It's an uncomfortable conversation. But the manager later tells Carla, "At least they were honest. We can coach that."

In the next safety meeting, you're the one explaining why early reporting matters.

''On the factory floor, the real test isn't never making a mistake—it's how quickly you stop repeating one.''

[[Replay the story|Start]]

:: Deflect
You claim you were never told about the issue.

Later, Miguel hears what you said. He stops bringing potential problems to you directly.

Small issues start bypassing you and growing into bigger ones.

''When people stop telling you about problems, that doesn't mean the problems stopped—only your ability to see them did.''

[[Replay the story|Start]]

:: EscalateWithMiguel
You say, "Let's call maintenance together. If it's a real issue, I want them to hear it from you too."

Maintenance confirms the sensor is failing intermittently and replaces it. You thank Miguel in front of the team for catching it early.

A week later, another operator brings you a different concern. "Figured you'd actually listen," they say.

''Safety culture doesn't start with posters; it starts with the first person who feels heard.''

[[Replay the story|Start]]

:: LogQuietly
You log the issue in the system but don't push for urgency. By the time maintenance checks the log three days later, the sensor has already failed and caused a one-hour stoppage.

Your note proves you saw the risk but didn't escalate it.

Carla points out, gently, that logging is not the same as leading.

[[Replay the story|Start]]

:: TeachMoment
You gather the team after the sensor is fixed.

"I know stopping the line isn't popular," you say, "but I'd rather explain a delay than an injury report. If something feels off, I want to hear it early—even if it turns out to be nothing."

Miguel nods. "Fair enough."

In the monthly safety report, your area has the most reported near-misses—and the fewest incidents.

''High-reporting teams aren't more dangerous; they're more honest.''

[[Replay the story|Start]]

:: StaySilent
You let management send a generic memo about "equipment checks" and say nothing specific about the event.

To the team, it looks like another mysterious decision from somewhere above them.

The next time someone sees a flickering warning light, they quietly look for a workaround instead of a supervisor.

[[Replay the story|Start]]`
  },
  
  'training-inbox-training-suspicious-invoice': {
    title: 'Inbox Training: The Suspicious Invoice',
    category: 'Training, Education, and Onboarding',
    description: 'You receive an urgent invoice from a supplier. Is it legitimate or a phishing attack? Your decision matters.',
    twee: `:: StoryTitle
Inbox Training: The Suspicious Invoice

:: Start
You are a new accounts payable clerk at a growing company.

Your inbox pings with an email titled:

"**Overdue Invoice - ACTION REQUIRED**"

It appears to be from a long-time supplier, NorthBridge Components, and includes a PDF attachment.

Your manager is at an offsite meeting for the day.

[[Open the PDF and process it quickly|ProcessFast]]
[[Compare the invoice to past records first|VerifyRecords]]
[[Forward the email to IT as suspicious|ReportIT]]

:: ProcessFast
You open the PDF. It looks legitimate: logo, address, item list—everything.

You enter the details into the payment system and schedule the transfer for this afternoon.

Two hours later, IT sends a company-wide email: some suppliers have reported phishing attempts with fake invoices.

You suddenly feel very warm.

[[Immediately try to cancel the payment|CancelPayment]]
[[Assume it's fine because it looked real|AssumeFine]]

:: VerifyRecords
You check your ERP system for past invoices from NorthBridge.

You notice something odd: the bank account number in this email is different from the last three NorthBridge invoices.

The email also spells your company name slightly wrong in the header.

[[Call NorthBridge using the phone number on file|CallSupplier]]
[[Reply to the email and ask for clarification|ReplyToEmail]]

:: ReportIT
You forward the email to IT with a short note: "This might be legit, but something about the tone feels off. Can you check?"

They thank you and ask you not to open the attachment.

Thirty minutes later, IT confirms it's a phishing attempt using a cloned supplier domain.

The CISO mentions your name in the next town-hall meeting as an example of "exactly the kind of caution we want."

''Sometimes the most visible heroism is the trouble that never happens.''

[[Replay the story|Start]]

:: CancelPayment
You race to the payment system and flag the transfer as suspicious.

Luckily, the bank has not processed it yet. The payment is halted pending verification.

IT confirms it was a phishing attack. Your quick action turned a mistake into a near-miss.

In the post-incident review, you're honest about what happened—and your story becomes part of the formal training.

''Good security culture doesn't require perfection, just fast, honest correction.''

[[Replay the story|Start]]

:: AssumeFine
You ignore the IT warning and let the payment go through.

The next day, the real NorthBridge calls: "We haven't been paid yet. And by the way, our domain has been spoofed."

Finance discovers the fraudulent transfer. The amount isn't catastrophic, but the embarrassment is.

Your manager has an uncomfortable conversation with you about "slowing down to go fast."

[[Replay the story|Start]]

:: CallSupplier
You call NorthBridge using the number in your vendor system.

"That invoice number doesn't match anything we've sent," their rep says. "And we definitely didn't change bank accounts."

They ask you to forward the phishing email so their security team can investigate.

IT thanks you for verifying before paying.

''Trust is good. Verification is better. Phone numbers in your own system are best.''

[[Replay the story|Start]]

:: ReplyToEmail
You reply: "Hi, just confirming this invoice before payment. Can you resend from an official address on file?"

Minutes later, you get a response:

"Payment must be processed today. Do not contact our accounting team; they are unavailable."

The tone is pushy and strange.

By the time you forward this to IT, another employee in a different department has already paid a similar fake invoice.

''Attackers abuse urgency because it works—especially on people trying to be helpful quickly.''

[[Replay the story|Start]]`
  },
  
  'usage-type-demo': {
    title: 'Usage Type Demo',
    category: 'Interactive Demo',
    description: 'A simple demo showing the Left/Right layout with interactive navigation.',
    twee: `:: StoryTitle
Usage Type Demo

:: Start
Usage Type

How do you want to test this?

[[{{{left}}} Consumer->Consumer]]

[[{{{right}}} Business->Business]]

:: Consumer
''Personal Stories & Creative Projects''

Share interactive adventures, fictional narratives, family stories, or community projects.

Perfect for fiction writers, family historians, game creators, and hobbyist storytellers.

[[{{{right}}} Back->Start]]

:: Business
''Business Case Studies''

Show prospects why your solution matters with interactive decision-based stories.

Perfect for consultants, SaaS companies, service providers, and sales teams.

[[{{{right}}} Back->Start]]
`
  }
};

export default exampleStories;
